import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  ExpandMore,
  ContentCopy,
  CalendarMonth,
  CheckCircle,
  Warning,
  CheckCircleOutline,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { useEffect } from 'react';
import { useCloneClasses } from '../../hooks/useCloneClasses';
import { subtleFieldBg } from '../../utils/rowHighlight';

interface CloneClassDialogProps {
  open: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
  sourceWeekStart: string;
  sourceWeekLabel: string;
}

const formatDateOnlyForDisplay = (dateKey: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateKey);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : dateKey;
};

const formatTargetWeekRange = (dateKey: string): string => {
  const startDate = dayjs(dateKey);
  if (!startDate.isValid()) return 'Select a target date';

  return `Week ${startDate.format('DD/MM/YYYY')} to ${startDate
    .add(6, 'day')
    .format('DD/MM/YYYY')}`;
};

const CloneClassDialog = ({
  open,
  handleClose,
  onSuccess,
  sourceWeekStart,
  sourceWeekLabel,
}: CloneClassDialogProps) => {
  const {
    sourceWeekDetails,
    loading,
    loadingWeekDetails,
    formData,
    cloneResult,
    fetchSourceWeekDetails,
    setSourceWeek,
    updateFormData,
    toggleClassSelection,
    toggleDaySelection,
    handleClone,
    resetForm,
  } = useCloneClasses();

  // Set sourceWeekStart when dialog opens
  useEffect(() => {
    if (open && sourceWeekStart) {
      setSourceWeek(sourceWeekStart);
    }
  }, [open, sourceWeekStart, setSourceWeek]);

  // Fetch week details when source week changes and not cloning all
  useEffect(() => {
    if (formData.sourceWeekStart && !formData.cloneAll) {
      fetchSourceWeekDetails(formData.sourceWeekStart);
    }
  }, [formData.sourceWeekStart, formData.cloneAll]);

  const handleSubmit = async () => {
    const success = await handleClone();
    if (success && onSuccess) {
      onSuccess();
    }
  };

  const handleDialogClose = () => {
    resetForm();
    handleClose();
  };

  // Get level color helper
  const getLevelColor = (levelName: string): { bg: string; text: string } => {
    const lightenColor = (hex: string, percent = 50): string => {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
      const g =
        ((num >> 8) & 0x00ff) +
        Math.round((255 - ((num >> 8) & 0x00ff)) * (percent / 100));
      const b =
        (num & 0x0000ff) +
        Math.round((255 - (num & 0x0000ff)) * (percent / 100));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };

    const levelColorMap: Record<string, string> = {
      Starfish: '#ff94ca',
      Seahorse: '#fff056',
      Seal: '#88fa4e',
      Barracuda: '#55c1ff',
      Swordfish: '#ff968d',
      Dolphin: '#d31876',
      Development: '#102E50',
      Purple: '#522546',
      Bronze: '#706D54',
      Silver: '#818C78',
      Gold: '#EFB036',
      Elite: '#EB5A3C',
    };

    const darkenColor = (hex: string, percent = 30): string => {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = Math.max(
        0,
        (num >> 16) - Math.round((num >> 16) * (percent / 100)),
      );
      const g = Math.max(
        0,
        ((num >> 8) & 0x00ff) -
          Math.round(((num >> 8) & 0x00ff) * (percent / 100)),
      );
      const b = Math.max(
        0,
        (num & 0x0000ff) - Math.round((num & 0x0000ff) * (percent / 100)),
      );
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };

    const baseColor = levelColorMap[levelName];
    if (baseColor) {
      return {
        bg: lightenColor(baseColor, 80),
        text: darkenColor(baseColor, 30),
      };
    }

    return { bg: '#F5F5F5', text: '#616161' };
  };

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ] as const;

  const sourceDate = dayjs(formData.sourceWeekStart);
  const minimumTargetDate = sourceDate.isValid()
    ? sourceDate.add(1, 'day')
    : undefined;

  // If clone result exists, show results view
  if (cloneResult) {
    const skippedStudents = cloneResult.skippedStudents || [];
    const skippedStudentCount =
      cloneResult.summary.studentsSkipped ?? skippedStudents.length;

    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              maxHeight: '80vh',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            fontSize: '1.5rem',
          }}
        >
          <CheckCircle />
          Clone Results
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '12px',
              border: '2px solid #10b981',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              color="success.main"
              mb={2}
            >
              Summary
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Classes Cloned
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {cloneResult.summary.totalCloned}
                </Typography>
              </Box>
              {cloneResult.summary.coachesCloned > 0 && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Coaches Assigned
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="success.main"
                  >
                    {cloneResult.summary.coachesCloned}
                  </Typography>
                </Box>
              )}
              {cloneResult.summary.studentsCloned > 0 && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Students Enrolled
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="success.main"
                  >
                    {cloneResult.summary.studentsCloned}
                  </Typography>
                </Box>
              )}
              {cloneResult.summary.conflictsFound > 0 && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Conflicts Found
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#dc2626">
                    {cloneResult.summary.conflictsFound}
                  </Typography>
                </Box>
              )}
              {skippedStudentCount > 0 && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Students Skipped
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#d97706">
                    {skippedStudentCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Conflicts */}
          {cloneResult.conflicts.length > 0 && (
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Conflicts Detected
              </AlertTitle>
              <List dense>
                {cloneResult.conflicts.map((conflict, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText
                      primary={`${conflict.className} - ${formatDateOnlyForDisplay(conflict.classDate)}`}
                      secondary={conflict.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {skippedStudents.length > 0 && (
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{ mb: 3, borderRadius: '12px' }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                Students Not Enrolled
              </AlertTitle>
              <Typography variant="body2" mb={1}>
                These students were skipped because their registration was
                inactive or did not cover the target class date.
              </Typography>
              <List dense>
                {skippedStudents.map((student) => (
                  <ListItem
                    key={`${student.sourceClassId}-${student.userId}-${student.registrationId}`}
                    sx={{ px: 0 }}
                  >
                    <ListItemText
                      primary={`User #${student.userId} - Registration #${student.registrationId}`}
                      secondary={student.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* Created Classes */}
          {cloneResult.created.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Created Classes ({cloneResult.created.length})
              </Typography>
              <List dense>
                {cloneResult.created.map((cls) => (
                  <Paper
                    key={cls.id}
                    elevation={0}
                    sx={{
                      mb: 1,
                      p: 2,
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: subtleFieldBg,
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {cls.name}
                        </Typography>
                        <Box display="flex" gap={1} mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            {formatDateOnlyForDisplay(cls.classDate)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {cls.startTime} - {cls.endTime}
                          </Typography>
                        </Box>
                      </Box>
                      {cls.level && (
                        <Chip
                          label={cls.level}
                          size="small"
                          sx={{
                            backgroundColor: getLevelColor(cls.level).bg,
                            color: getLevelColor(cls.level).text,
                            fontWeight: 600,
                            border: `2px solid ${getLevelColor(cls.level).text}`,
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleDialogClose}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              fontWeight: 600,
              px: 4,
              borderRadius: '10px',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 700,
          fontSize: '1.5rem',
        }}
      >
        <ContentCopy />
        Clone Classes
      </DialogTitle>
      <DialogContent sx={{ mt: 3, pt: 3 }}>
        <Typography variant="body2" color="textSecondary" mb={3}>
          Clone classes from one week to another, including optional coach
          assignments and student enrollments.
        </Typography>

        {/* Source Week Display */}
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CalendarMonth sx={{ color: '#3b82f6', fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary.main"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Source Week
            </Typography>
          </Box>
          <Typography
            variant="h6"
            fontWeight={600}
            color="text.primary"
            sx={{ pl: 0.5 }}
          >
            {sourceWeekLabel}
          </Typography>
        </Box>

        {/* Target Week Display */}
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: '2px solid #10b981',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CalendarMonth sx={{ color: '#10b981', fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="success.main"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Target Week
            </Typography>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                formData.targetWeekStart
                  ? dayjs(formData.targetWeekStart)
                  : null
              }
              minDate={minimumTargetDate}
              disabled={loading}
              format="DD/MM/YYYY"
              label="Target week start"
              onChange={(date) => {
                updateFormData({
                  targetWeekStart:
                    date?.isValid() && date ? date.format('YYYY-MM-DD') : '',
                });
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  inputProps: { 'aria-label': 'Target week start' },
                },
              }}
            />
          </LocalizationProvider>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Clone period: {formatTargetWeekRange(formData.targetWeekStart)}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Choose any future date as the start of the target week. The clone
            covers seven consecutive days from that date; matching classes are
            reported as conflicts and are not duplicated.
          </Alert>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Clone Options */}
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Clone Options
          </Typography>
          <RadioGroup
            value={formData.cloneAll ? 'all' : 'selected'}
            onChange={(e) =>
              updateFormData({
                cloneAll: e.target.value === 'all',
                selectedClassIds: [],
              })
            }
          >
            <FormControlLabel
              disabled={loading}
              value="all"
              control={<Radio />}
              label="Clone all classes from the source week"
            />
            <FormControlLabel
              disabled={loading}
              value="selected"
              control={<Radio />}
              label="Select specific classes to clone"
            />
          </RadioGroup>
        </Box>

        {/* Class Selection (when not cloning all) */}
        {!formData.cloneAll && formData.sourceWeekStart && (
          <Box mb={3}>
            {loadingWeekDetails ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : sourceWeekDetails ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Select Classes ({formData.selectedClassIds.length} selected)
                </Typography>
                {daysOfWeek.map((day) => {
                  const dayClasses =
                    sourceWeekDetails.groupedByDay?.[day] || [];
                  if (!dayClasses || dayClasses.length === 0) return null;

                  const dayClassIds = dayClasses.map((c) => c.id);
                  const allSelected = dayClassIds.every((id: number) =>
                    formData.selectedClassIds.includes(id),
                  );

                  return (
                    <Accordion key={day} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                if (loading) return;
                                toggleDaySelection(day);
                              }}
                              sx={{
                                cursor: loading ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                '&:hover': {
                                  opacity: 0.7,
                                },
                              }}
                            >
                              {allSelected ? (
                                <CheckCircle
                                  sx={{ color: '#3b82f6', fontSize: 24 }}
                                />
                              ) : dayClassIds.some((id: number) =>
                                  formData.selectedClassIds.includes(id),
                                ) ? (
                                <CheckCircleOutline
                                  sx={{ color: '#3b82f6', fontSize: 24 }}
                                />
                              ) : (
                                <RadioButtonUnchecked
                                  sx={{ color: 'text.secondary', fontSize: 24 }}
                                />
                              )}
                            </Box>
                            <Typography fontWeight={600}>{day}</Typography>
                            <Chip
                              label={`${dayClasses.length} ${dayClasses.length === 1 ? 'class' : 'classes'}`}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {dayClasses.map((cls) => (
                            <Paper
                              key={cls.id}
                              elevation={0}
                              sx={{
                                mb: 1,
                                p: 1.5,
                                border: formData.selectedClassIds.includes(
                                  cls.id,
                                )
                                  ? '2px solid #3b82f6'
                                  : '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: loading ? 'default' : 'pointer',
                                '&:hover': {
                                  backgroundColor: subtleFieldBg,
                                },
                              }}
                              onClick={() => {
                                if (!loading) toggleClassSelection(cls.id);
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Box display="flex" alignItems="center" gap={2}>
                                  {formData.selectedClassIds.includes(
                                    cls.id,
                                  ) ? (
                                    <CheckCircle
                                      sx={{ color: '#3b82f6', fontSize: 24 }}
                                    />
                                  ) : (
                                    <RadioButtonUnchecked
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: 24,
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                    >
                                      {cls.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {cls.startTime} - {cls.endTime}
                                    </Typography>
                                  </Box>
                                </Box>
                                {cls.level && (
                                  <Chip
                                    label={cls.level}
                                    size="small"
                                    sx={{
                                      backgroundColor: getLevelColor(cls.level)
                                        .bg,
                                      color: getLevelColor(cls.level).text,
                                      fontWeight: 600,
                                      border: `2px solid ${getLevelColor(cls.level).text}`,
                                    }}
                                  />
                                )}
                              </Box>
                            </Paper>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="info">
                Select a source week to view available classes
              </Alert>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Additional Options */}
        <Box>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Additional Options
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.cloneCoaches}
                disabled={loading}
                onChange={(e) =>
                  updateFormData({ cloneCoaches: e.target.checked })
                }
              />
            }
            label="Clone coach assignments"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.cloneStudents}
                disabled={loading}
                onChange={(e) =>
                  updateFormData({ cloneStudents: e.target.checked })
                }
              />
            }
            label="Clone student enrollments"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleDialogClose}
          disabled={loading}
          sx={{
            fontWeight: 600,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData.sourceWeekStart ||
            !formData.targetWeekStart ||
            (!formData.cloneAll && formData.selectedClassIds.length === 0)
          }
          sx={{
            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            borderRadius: '10px',
            '&:hover': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            },
            '&:disabled': {
              background: '#e5e7eb',
              color: 'text.secondary',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Clone Classes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneClassDialog;
